import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { 
  FaPlus, FaEdit, FaTrash, FaCheck, FaExclamationTriangle, 
  FaClock, FaUser, FaUsers, FaEuroSign, FaCalendarAlt, FaRocket,
  FaServer, FaGlobe, FaSearch, FaLayerGroup, FaImage
} from 'react-icons/fa';
import { usePermission } from '../../../hooks/usePermission';

const Projects = () => {
    const { can } = usePermission();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentProject, setCurrentProject] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        client_name: '',
        service_type: '',
        priority: 'Medium',
        status: 'Not Started',
        progress_percentage: 0,
        project_phase: 'Planning',
        deadline: '',
        responsible_person: '',
        team_members: [],
        total_price: '',
        amount_paid: 0,
        payment_status: 'Not Paid',
        hosting_paid: false,
        hosting_active: false,
        domain_purchased: false,
        sections: [], // Changed from key_deliverables to sections
        blockers: [],
        next_action: ''
    });

    const [showSectionModal, setShowSectionModal] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [sectionFormData, setSectionFormData] = useState({
        title: '',
        description: '',
        photos: [],
        deliverables: []
    });

    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        priority: 'all',
        phase: 'all',
        payment_status: 'all'
    });

    // Options for dropdowns
    const serviceTypes = [
        'Website Development', 'Mobile App', 'E-commerce', 'Branding', 
        'UI/UX Design', 'Digital Marketing', 'SEO', 'Maintenance', 'Consulting'
    ];
    
    const priorities = ['Low', 'Medium', 'High', 'Critical'];
    const statuses = ['Not Started', 'In Progress', 'Completed', 'At Risk', 'Waiting'];
    const phases = ['Planning', 'Development', 'Review', 'Final'];
    const paymentStatuses = ['Not Paid', 'Partial', 'Paid'];

    useEffect(() => {
        fetchProjects();
    }, []);

    // Cleanup effect to restore body scroll on unmount
    useEffect(() => {
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);
    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${SERVER}/projects`, {
                withCredentials: true
            });
            setProjects(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                team_members: Array.isArray(formData.team_members) ? formData.team_members : 
                    formData.team_members.split(',').map(m => m.trim()).filter(m => m),
                sections: formData.sections, // Keep sections as is
                blockers: Array.isArray(formData.blockers) ? formData.blockers :
                    formData.blockers.split(',').map(b => b.trim()).filter(b => b)
            };

            if (currentProject) {
                if (!can('projects.edit')) return alert("Permission denied");
                await axios.put(`${SERVER}/projects/${currentProject.id}`, submitData, {
                    withCredentials: true
                });
            } else {
                if (!can('projects.create')) return alert("Permission denied");
                await axios.post(`${SERVER}/projects`, submitData, {
                    withCredentials: true
                });
            }
            fetchProjects();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving project:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!can('projects.delete')) {
            alert("You don't have permission to delete projects.");
            return;
        }
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await axios.delete(`${SERVER}/projects/${id}`, {
                    withCredentials: true
                });
                fetchProjects();
            } catch (error) {
                console.error('Error deleting project:', error);
            }
        }
    };
    const handleOpenModal = (project = null) => {
        if (project && !can('projects.edit')) {
            alert("You don't have permission to edit projects.");
            return;
        }
        if (!project && !can('projects.create')) {
             alert("You don't have permission to create projects.");
             return;
        }

        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';

        if (project) {
            setCurrentProject(project);
            setFormData({
                name: project.name || '',
                client_name: project.client_name || '',
                service_type: project.service_type || '',
                priority: project.priority || 'Medium',
                status: project.status || 'Not Started',
                progress_percentage: project.progress_percentage || 0,
                project_phase: project.project_phase || 'Planning',
                deadline: project.deadline ? project.deadline.split('T')[0] : '',
                responsible_person: project.responsible_person || '',
                team_members: Array.isArray(project.team_members) ? project.team_members.join(', ') : '',
                total_price: project.total_price || '',
                amount_paid: project.amount_paid || 0,
                payment_status: project.payment_status || 'Not Paid',
                hosting_paid: project.hosting_paid || false,
                hosting_active: project.hosting_active || false,
                domain_purchased: project.domain_purchased || false,
                sections: project.sections || [],
                blockers: Array.isArray(project.blockers) ? project.blockers.join(', ') : '',
                next_action: project.next_action || ''
            });
        } else {
            setCurrentProject(null);
            setFormData({
                name: '',
                client_name: '',
                service_type: '',
                priority: 'Medium',
                status: 'Not Started',
                progress_percentage: 0,
                project_phase: 'Planning',
                deadline: '',
                responsible_person: '',
                team_members: '',
                total_price: '',
                amount_paid: 0,
                payment_status: 'Not Paid',
                hosting_paid: false,
                hosting_active: false,
                domain_purchased: false,
                sections: [],
                blockers: '',
                next_action: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        // Restore body scroll when modal is closed
        document.body.style.overflow = 'unset';
        setIsModalOpen(false);
        setCurrentProject(null);
    };

    // Section management functions
    const openSectionModal = (section = null) => {
        if (section) {
            setSectionFormData({
                title: section.title || '',
                description: section.description || '',
                photos: [],
                deliverables: section.deliverables || []
            });
            setEditingSection(section);
        } else {
            setSectionFormData({
                title: '',
                description: '',
                photos: [],
                deliverables: []
            });
            setEditingSection(null);
        }
        setShowSectionModal(true);
    };

    const handleSectionSubmit = (e) => {
        e.preventDefault();
        
        const newSection = {
            id: editingSection ? editingSection.id : Date.now(),
            title: sectionFormData.title,
            description: sectionFormData.description,
            photos: sectionFormData.photos,
            deliverables: sectionFormData.deliverables
        };

        let updatedSections;
        if (editingSection) {
            updatedSections = formData.sections.map(section => 
                section.id === editingSection.id ? newSection : section
            );
        } else {
            updatedSections = [...formData.sections, newSection];
        }

        setFormData({ ...formData, sections: updatedSections });
        setShowSectionModal(false);
        setEditingSection(null);
    };

    const handleDeleteSection = (sectionId) => {
        const updatedSections = formData.sections.filter(section => section.id !== sectionId);
        setFormData({ ...formData, sections: updatedSections });
    };

    // Section deliverable helpers
    const addDeliverable = () => {
        setSectionFormData({
            ...sectionFormData,
            deliverables: [...sectionFormData.deliverables, { title: '' }]
        });
    };

    const updateDeliverable = (index, value) => {
        const updatedDeliverables = [...sectionFormData.deliverables];
        updatedDeliverables[index] = { title: value };
        setSectionFormData({
            ...sectionFormData,
            deliverables: updatedDeliverables
        });
    };

    const removeDeliverable = (index) => {
        const updatedDeliverables = sectionFormData.deliverables.filter((_, i) => i !== index);
        setSectionFormData({
            ...sectionFormData,
            deliverables: updatedDeliverables
        });
    };
    // Utility functions and components
    const glassCardClass = "bg-white/60 backdrop-blur-xl border border-white/40 shadow-xl rounded-2xl";
    const glassInputClass = "bg-white/50 border border-white/60 focus:ring-2 focus:ring-blue-500/50 rounded-xl backdrop-blur-md";
    const buttonClass = "px-4 py-2 rounded-xl font-medium transition-all shadow-sm hover:shadow-md active:scale-95";

    const StatusBadge = ({ status, type = 'status' }) => {
        let color = 'bg-gray-100/80 text-gray-600';
        
        if (type === 'status') {
            if (status === 'Completed') color = 'bg-green-100/80 text-green-700';
            if (status === 'In Progress') color = 'bg-blue-100/80 text-blue-700';
            if (status === 'At Risk') color = 'bg-red-100/80 text-red-700';
            if (status === 'Waiting') color = 'bg-yellow-100/80 text-yellow-700';
        } else if (type === 'priority') {
            if (status === 'Critical') color = 'bg-red-100/80 text-red-700';
            if (status === 'High') color = 'bg-orange-100/80 text-orange-700';
            if (status === 'Medium') color = 'bg-yellow-100/80 text-yellow-700';
            if (status === 'Low') color = 'bg-green-100/80 text-green-700';
        } else if (type === 'payment') {
            if (status === 'Paid') color = 'bg-green-100/80 text-green-700';
            if (status === 'Partial') color = 'bg-yellow-100/80 text-yellow-700';
            if (status === 'Not Paid') color = 'bg-red-100/80 text-red-700';
        }

        return (
            <span className={`inline-flex px-2 py-1 rounded-lg text-xs font-bold backdrop-blur-sm border border-white/20 whitespace-nowrap ${color}`}>
                {status}
            </span>
        );
    };

    const ProgressBar = ({ percentage }) => (
        <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${percentage}%` }}
            ></div>
        </div>
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', { 
            style: 'currency', 
            currency: 'EUR' 
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };
    // Filter projects
    const filteredProjects = projects.filter(project => {
        if (filters.search && !project.name.toLowerCase().includes(filters.search.toLowerCase()) &&
            !project.client_name?.toLowerCase().includes(filters.search.toLowerCase())) {
            return false;
        }
        if (filters.status !== 'all' && project.status !== filters.status) return false;
        if (filters.priority !== 'all' && project.priority !== filters.priority) return false;
        if (filters.phase !== 'all' && project.project_phase !== filters.phase) return false;
        if (filters.payment_status !== 'all' && project.payment_status !== filters.payment_status) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn p-6 rounded-3xl bg-gradient-to-br from-indigo-50/50 via-purple-50/50 to-teal-50/50 min-h-screen">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                     <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                        Gestion des Projets
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Suivi complet des projets clients</p>
                </div>
                {can('projects.create') && (
                    <button 
                        onClick={() => handleOpenModal()} 
                        className={`${buttonClass} bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2`}
                    >
                        <FaPlus /> Nouveau Projet
                    </button>
                )}
            </div>

            {/* Filter Controls */}
            <div className={`${glassCardClass} p-6`}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaSearch className="inline mr-1" /> Recherche
                        </label>
                        <input
                            type="text"
                            placeholder="Nom du projet ou client..."
                            value={filters.search}
                            onChange={(e) => setFilters({...filters, search: e.target.value})}
                            className={`w-full p-2 ${glassInputClass} text-sm`}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({...filters, status: e.target.value})}
                            className={`w-full p-2 ${glassInputClass} text-sm`}
                        >
                            <option value="all">Tous les statuts</option>
                            {statuses.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                        <select
                            value={filters.priority}
                            onChange={(e) => setFilters({...filters, priority: e.target.value})}
                            className={`w-full p-2 ${glassInputClass} text-sm`}
                        >
                            <option value="all">Toutes priorités</option>
                            {priorities.map(priority => (
                                <option key={priority} value={priority}>{priority}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
                        <select
                            value={filters.phase}
                            onChange={(e) => setFilters({...filters, phase: e.target.value})}
                            className={`w-full p-2 ${glassInputClass} text-sm`}
                        >
                            <option value="all">Toutes phases</option>
                            {phases.map(phase => (
                                <option key={phase} value={phase}>{phase}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Paiement</label>
                        <select
                            value={filters.payment_status}
                            onChange={(e) => setFilters({...filters, payment_status: e.target.value})}
                            className={`w-full p-2 ${glassInputClass} text-sm`}
                        >
                            <option value="all">Tous paiements</option>
                            {paymentStatuses.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            {/* Projects Table */}
            <div className={`${glassCardClass} overflow-hidden`}>
                <div className="overflow-x-auto">
                    <table className="min-w-full table-fixed">
                        <thead className="bg-white/30 backdrop-blur-sm">
                            <tr>
                                <th className="w-48 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Projet & Client</th>
                                <th className="w-40 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Service & Priorité</th>
                                <th className="w-36 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Statut & Phase</th>
                                <th className="w-32 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Avancement</th>
                                <th className="w-44 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Échéance & Responsable</th>
                                <th className="w-36 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Financier</th>
                                <th className="w-40 px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Technique</th>
                                <th className="w-24 px-4 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/20">
                            {filteredProjects.map((project) => (
                                <tr key={project.id} className="hover:bg-white/40 transition-colors">
                                    {/* Project & Client */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-1">
                                            <div className="font-bold text-gray-800 text-sm truncate" title={project.name}>
                                                {project.name}
                                            </div>
                                            <div className="text-xs text-gray-600 flex items-center gap-1">
                                                <FaUser className="flex-shrink-0" />
                                                <span className="truncate">{project.client_name || 'Client non défini'}</span>
                                            </div>
                                            {project.next_action && (
                                                <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded truncate" title={project.next_action}>
                                                    <FaClock className="inline mr-1 flex-shrink-0" />
                                                    <span className="truncate">{project.next_action}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Service & Priority */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-2">
                                            <div className="text-sm text-gray-700 font-medium truncate" title={project.service_type}>
                                                {project.service_type || 'Service non défini'}
                                            </div>
                                            <StatusBadge status={project.priority} type="priority" />
                                        </div>
                                    </td>

                                    {/* Status & Phase */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-2">
                                            <StatusBadge status={project.status} type="status" />
                                            <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded text-center">
                                                {project.project_phase}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Progress */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-2">
                                            <div className="w-full">
                                                <ProgressBar percentage={project.progress_percentage || 0} />
                                            </div>
                                            <div className="text-xs text-gray-500 font-semibold text-center">
                                                {project.progress_percentage || 0}% terminé
                                            </div>
                                        </div>
                                    </td>

                                    {/* Deadline & Responsible */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-1 text-sm text-gray-600">
                                                <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                                                <span className="truncate">{formatDate(project.deadline)}</span>
                                            </div>
                                            <div className="text-xs text-gray-600 flex items-center gap-1">
                                                <FaUser className="text-gray-400 flex-shrink-0" />
                                                <span className="truncate">{project.responsible_person || 'Non assigné'}</span>
                                            </div>
                                            {project.team_members && project.team_members.length > 0 && (
                                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                                    <FaUsers className="flex-shrink-0" />
                                                    <span className="truncate">
                                                        {Array.isArray(project.team_members) ? 
                                                            project.team_members.slice(0, 2).join(', ') + 
                                                            (project.team_members.length > 2 ? '...' : '') :
                                                            'Équipe assignée'
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Financial */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-2">
                                            <div className="text-sm font-semibold text-gray-800">
                                                {formatCurrency(project.total_price)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Payé: {formatCurrency(project.amount_paid)}
                                            </div>
                                            <StatusBadge status={project.payment_status} type="payment" />
                                        </div>
                                    </td>

                                    {/* Technical */}
                                    <td className="px-4 py-4">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1 text-xs">
                                                <FaServer className={`flex-shrink-0 ${project.hosting_paid ? 'text-green-500' : 'text-red-400'}`} />
                                                <span className={`truncate ${project.hosting_paid ? 'text-green-700' : 'text-red-600'}`}>
                                                    {project.hosting_paid ? 'Payé' : 'Non payé'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs">
                                                <FaRocket className={`flex-shrink-0 ${project.hosting_active ? 'text-green-500' : 'text-red-400'}`} />
                                                <span className={`truncate ${project.hosting_active ? 'text-green-700' : 'text-red-600'}`}>
                                                    {project.hosting_active ? 'Actif' : 'Inactif'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs">
                                                <FaGlobe className={`flex-shrink-0 ${project.domain_purchased ? 'text-green-500' : 'text-red-400'}`} />
                                                <span className={`truncate ${project.domain_purchased ? 'text-green-700' : 'text-red-600'}`}>
                                                    {project.domain_purchased ? 'OK' : 'Manquant'}
                                                </span>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-4 py-4 text-right">
                                        <div className="flex justify-end space-x-1">
                                            {can('projects.edit') && (
                                                <button 
                                                    onClick={() => handleOpenModal(project)} 
                                                    className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-100 rounded-lg transition-colors"
                                                    title="Modifier le projet"
                                                >
                                                    <FaEdit />
                                                </button>
                                            )}
                                            {can('projects.delete') && (
                                                <button 
                                                    onClick={() => handleDelete(project.id)} 
                                                    className="text-red-600 hover:text-red-800 p-2 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="Supprimer le projet"
                                                >
                                                    <FaTrash />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {/* Empty State */}
                    {filteredProjects.length === 0 && (
                        <div className="text-center py-12">
                            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaExclamationTriangle className="text-3xl text-slate-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-2">Aucun projet trouvé</h3>
                            <p className="text-slate-600 mb-4">
                                {projects.length === 0 
                                    ? "Commencez par créer votre premier projet." 
                                    : "Aucun projet ne correspond aux filtres sélectionnés."
                                }
                            </p>
                            {can('projects.create') && projects.length === 0 && (
                                <button 
                                    onClick={() => handleOpenModal()} 
                                    className={`${buttonClass} bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 mx-auto`}
                                >
                                    <FaPlus /> Créer le premier projet
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Modal */}
            {isModalOpen && (
                <>
                    {/* Modal Backdrop */}
                    <div 
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-fadeIn modal-backdrop" 
                        onClick={handleCloseModal}
                    />
                    
                    {/* Modal Content */}
                    <div 
                        className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none modal-content" 
                    >
                        <div 
                            className={`${glassCardClass} w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8 animate-slideUp relative pointer-events-auto`}
                            onClick={(e) => e.stopPropagation()}
                        >
                        <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-6">
                            {currentProject ? 'Modifier le Projet' : 'Nouveau Projet'}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Information Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaUser className="text-blue-600" />
                                    Informations de Base
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nom du Projet <span className="text-red-500">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.name} 
                                            onChange={(e) => setFormData({...formData, name: e.target.value})} 
                                            required 
                                            placeholder="Ex: Site web e-commerce"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nom du Client <span className="text-red-500">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.client_name} 
                                            onChange={(e) => setFormData({...formData, client_name: e.target.value})} 
                                            required 
                                            placeholder="Ex: Entreprise ABC"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Type de Service</label>
                                        <select 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.service_type} 
                                            onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                                        >
                                            <option value="">Sélectionner un service</option>
                                            {serviceTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Limite</label>
                                        <input 
                                            type="date" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.deadline} 
                                            onChange={(e) => setFormData({...formData, deadline: e.target.value})} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Project Status Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaClock className="text-green-600" />
                                    Statut du Projet
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                                        <select 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.priority} 
                                            onChange={(e) => setFormData({...formData, priority: e.target.value})}
                                        >
                                            {priorities.map(priority => (
                                                <option key={priority} value={priority}>{priority}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                        <select 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.status} 
                                            onChange={(e) => setFormData({...formData, status: e.target.value})}
                                        >
                                            {statuses.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Phase du Projet</label>
                                        <select 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.project_phase} 
                                            onChange={(e) => setFormData({...formData, project_phase: e.target.value})}
                                        >
                                            {phases.map(phase => (
                                                <option key={phase} value={phase}>{phase}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Avancement (%)</label>
                                        <input 
                                            type="number" 
                                            min="0" 
                                            max="100" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.progress_percentage} 
                                            onChange={(e) => setFormData({...formData, progress_percentage: parseInt(e.target.value) || 0})} 
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Team and Responsibility Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaUsers className="text-purple-600" />
                                    Équipe et Responsabilités
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Responsable Principal</label>
                                        <input 
                                            type="text" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.responsible_person} 
                                            onChange={(e) => setFormData({...formData, responsible_person: e.target.value})} 
                                            placeholder="Nom du responsable du projet"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Membres de l'Équipe
                                            <span className="text-xs text-gray-500 ml-1">(séparés par virgules)</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.team_members} 
                                            onChange={(e) => setFormData({...formData, team_members: e.target.value})} 
                                            placeholder="John Doe, Marie Dupont, Pierre Martin..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Financial Information Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaEuroSign className="text-green-600" />
                                    Informations Financières
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Prix Total (€)</label>
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.total_price} 
                                            onChange={(e) => setFormData({...formData, total_price: e.target.value})} 
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Montant Payé (€)</label>
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.amount_paid} 
                                            onChange={(e) => setFormData({...formData, amount_paid: e.target.value})} 
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut de Paiement</label>
                                        <select 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.payment_status} 
                                            onChange={(e) => setFormData({...formData, payment_status: e.target.value})}
                                        >
                                            {paymentStatuses.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Technical Status Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaServer className="text-blue-600" />
                                    Statut Technique
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="flex items-center gap-3 p-3 bg-white/30 rounded-lg">
                                        <input 
                                            type="checkbox" 
                                            id="hosting_paid"
                                            className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500" 
                                            checked={formData.hosting_paid} 
                                            onChange={(e) => setFormData({...formData, hosting_paid: e.target.checked})} 
                                        />
                                        <label htmlFor="hosting_paid" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            <FaServer className="inline mr-2 text-blue-600" />
                                            Hébergement Payé
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-white/30 rounded-lg">
                                        <input 
                                            type="checkbox" 
                                            id="hosting_active"
                                            className="w-5 h-5 rounded text-green-600 focus:ring-green-500" 
                                            checked={formData.hosting_active} 
                                            onChange={(e) => setFormData({...formData, hosting_active: e.target.checked})} 
                                        />
                                        <label htmlFor="hosting_active" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            <FaRocket className="inline mr-2 text-green-600" />
                                            Hébergement Actif
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-white/30 rounded-lg">
                                        <input 
                                            type="checkbox" 
                                            id="domain_purchased"
                                            className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500" 
                                            checked={formData.domain_purchased} 
                                            onChange={(e) => setFormData({...formData, domain_purchased: e.target.checked})} 
                                        />
                                        <label htmlFor="domain_purchased" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            <FaGlobe className="inline mr-2 text-purple-600" />
                                            Domaine Acheté
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {/* Project Sections */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                        <FaLayerGroup className="text-purple-600" />
                                        Sections du Projet
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={() => openSectionModal()}
                                        className="flex items-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                                    >
                                        <FaPlus />
                                        Ajouter Section
                                    </button>
                                </div>
                                
                                {formData.sections.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <FaLayerGroup className="text-3xl mx-auto mb-2 opacity-50" />
                                        <p>Aucune section ajoutée. Cliquez sur "Ajouter Section" pour commencer.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {formData.sections.map((section, index) => (
                                            <div key={section.id} className="bg-white/30 rounded-lg p-4 border border-white/40">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h5 className="font-semibold text-gray-800 mb-1">{section.title}</h5>
                                                        <p className="text-sm text-gray-600 mb-2">{section.description}</p>
                                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                <FaImage />
                                                                {section.photos?.length || 0} photos
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <FaCheck />
                                                                {section.deliverables?.length || 0} livrables
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openSectionModal(section)}
                                                            className="text-blue-600 hover:text-blue-800 p-1"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteSection(section.id)}
                                                            className="text-red-600 hover:text-red-800 p-1"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Project Details Section */}
                            <div className="bg-white/20 rounded-xl p-4 border border-white/30">
                                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                    <FaExclamationTriangle className="text-orange-600" />
                                    Détails du Projet
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Bloquants Actuels
                                            <span className="text-xs text-gray-500 ml-1">(séparés par virgules)</span>
                                        </label>
                                        <textarea 
                                            className={`w-full p-3 ${glassInputClass} h-20 resize-none`} 
                                            value={formData.blockers} 
                                            onChange={(e) => setFormData({...formData, blockers: e.target.value})} 
                                            placeholder="Attente validation client, Problème technique serveur, Contenu manquant..."
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Prochaine Action Prioritaire</label>
                                        <input 
                                            type="text" 
                                            className={`w-full p-3 ${glassInputClass}`} 
                                            value={formData.next_action} 
                                            onChange={(e) => setFormData({...formData, next_action: e.target.value})} 
                                            placeholder="Appeler le client pour validation, Finaliser le design de la page d'accueil..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-white/20">
                                <button 
                                    type="button" 
                                    onClick={handleCloseModal} 
                                    className={`${buttonClass} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                                >
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className={`${buttonClass} bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90`}
                                >
                                    {currentProject ? 'Mettre à jour' : 'Créer le Projet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </>
            )}

            {/* Section Modal */}
            {showSectionModal && (
                <>
                    <div 
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-fadeIn modal-backdrop" 
                        onClick={() => setShowSectionModal(false)}
                    />
                    
                    <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto pointer-events-none modal-content">
                        <div 
                            className={`${glassCardClass} w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 animate-slideUp relative pointer-events-auto`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 mb-6">
                                {editingSection ? 'Modifier la Section' : 'Nouvelle Section'}
                            </h3>
                            
                            <form onSubmit={handleSectionSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Titre de la Section <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        className={`w-full p-3 ${glassInputClass}`} 
                                        value={sectionFormData.title} 
                                        onChange={(e) => setSectionFormData({...sectionFormData, title: e.target.value})} 
                                        required 
                                        placeholder="Ex: Branding, Site Web, Application Mobile"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea 
                                        className={`w-full p-3 ${glassInputClass} h-20 resize-none`} 
                                        value={sectionFormData.description} 
                                        onChange={(e) => setSectionFormData({...sectionFormData, description: e.target.value})} 
                                        placeholder="Description de cette section du projet..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        multiple
                                        className={`w-full p-3 ${glassInputClass}`} 
                                        onChange={(e) => setSectionFormData({...sectionFormData, photos: Array.from(e.target.files)})} 
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Sélectionnez plusieurs images pour cette section</p>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Livrables
                                        </label>
                                        <button
                                            type="button"
                                            onClick={addDeliverable}
                                            className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                                        >
                                            <FaPlus />
                                            Ajouter
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        {sectionFormData.deliverables.map((deliverable, index) => (
                                            <div key={index} className="flex gap-2 items-center">
                                                <input
                                                    type="text"
                                                    value={deliverable.title}
                                                    onChange={(e) => updateDeliverable(index, e.target.value)}
                                                    placeholder="Nom du livrable"
                                                    className={`flex-1 p-2 ${glassInputClass} text-sm`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeDeliverable(index)}
                                                    className="text-red-600 hover:text-red-800 p-2"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/20">
                                    <button 
                                        type="button" 
                                        onClick={() => setShowSectionModal(false)} 
                                        className={`${buttonClass} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        type="submit" 
                                        className={`${buttonClass} bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90`}
                                    >
                                        {editingSection ? 'Mettre à jour' : 'Ajouter Section'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Projects;